import { supabase } from '@/lib/supabaseClient';
import {
  loadCrewRoster,
  getCrewMember,
  findCrewMember,
  getCachedCrewRoster,
} from '@/lib/crewRoster';

/**
 * Phase 16 — Crew Clock now persists ALL state to Supabase so clock-in,
 * breaks, and clock-out sync across devices. The previous localStorage-only
 * model only saved completed shifts and broke cross-device sync.
 *
 * Storage model (extended `time_entries` table — see migration 005):
 *   id, crew_id, name, job, job_id, date,
 *   clock_in_at      timestamptz,
 *   clock_out_at     timestamptz,
 *   breaks           jsonb  (array of {start,end} ISO strings),
 *   current_break_start timestamptz,
 *   status           text   ('clocked_in' | 'on_break' | 'completed'),
 *   hours, cost,
 *   created_at
 *
 * Live state lookup uses an indexed predicate on (crew_id, status) so the
 * "find my active shift" query is cheap.
 */

/**
 * Detect Postgres "column does not exist" errors thrown when the schema
 * migration (sql/005_extend_time_entries.sql) hasn't been applied yet.
 * Returns a user-friendly Error so toasts/UI surface a clear next step.
 */
const wrapSchemaError = (error) => {
  if (!error) return error;
  // Postgres code 42703 = undefined_column. PostgREST surfaces this either
  // via the `code` field or in the message itself.
  const code = error.code || '';
  const msg = (error.message || '').toLowerCase();
  if (
    code === '42703' ||
    msg.includes('does not exist') ||
    msg.includes('could not find the')
  ) {
    const e = new Error(
      'Crew Clock schema migration not applied yet. Run sql/005_extend_time_entries.sql in your Supabase SQL editor.'
    );
    e.cause = error;
    e.schemaMigrationRequired = true;
    return e;
  }
  return error;
};

const TE_FIELDS =
  'id,crew_id,name,job,job_id,co_id,co_desc,date,clock_in_at,clock_out_at,breaks,current_break_start,status,hours,cost,created_at';

/* ----------------------------------------------------------------------------
 * Virtual jobs — SHOP and ADMIN are internal categories, not active jobs.
 * They must be selectable in the clock-in picker but they don't link to a
 * row in the `jobs` table.
 * ------------------------------------------------------------------------- */
export const VIRTUAL_JOBS = [
  {
    id: '__SHOP__',
    name: 'SHOP',
    client: 'Internal',
    address: '',
    status: 'virtual',
    virtual: true,
    label: 'SHOP',
    description: 'Shop / yard time (not tied to a client job)',
  },
  {
    id: '__ADMIN__',
    name: 'ADMIN',
    client: 'Internal',
    address: '',
    status: 'virtual',
    virtual: true,
    label: 'ADMIN',
    description: 'Admin / office time (not tied to a client job)',
  },
];

const isVirtualJobId = (id) =>
  id === '__SHOP__' || id === '__ADMIN__' || id == null || id === '';

/* ----------------------------------------------------------------------------
 * Optional offline-cache for the live timer (so ticks don't have to wait on
 * the network). Treated as a hint only — Supabase is always source of truth.
 * ------------------------------------------------------------------------- */
const LS_PREFIX = 'kocore.clock.cache.';
const lsKey = (crewId) => `${LS_PREFIX}${crewId}`;

const cacheActiveEntry = (crewId, entry) => {
  if (typeof window === 'undefined') return;
  try {
    if (entry == null) {
      window.localStorage.removeItem(lsKey(crewId));
    } else {
      window.localStorage.setItem(lsKey(crewId), JSON.stringify(entry));
    }
  } catch (e) {
    /* ignore quota errors */
  }
};

export const readCachedActiveEntry = (crewId) => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(lsKey(crewId));
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (e) {
    return null;
  }
};

/* ----------------------------------------------------------------------------
 * Helpers
 * ------------------------------------------------------------------------- */
export const formatHms = (totalSeconds) => {
  const s = Math.max(0, Math.floor(totalSeconds || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(h)}:${pad(m)}:${pad(sec)}`;
};

export const formatDecimalHours = (seconds) => {
  const h = Math.max(0, seconds || 0) / 3600;
  return `${h.toFixed(2)}h`;
};

export const formatUsd = (value, opts = {}) => {
  const n = Number(value);
  const safe = Number.isFinite(n) ? n : 0;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: opts.fraction ?? 2,
    minimumFractionDigits: opts.fraction ?? 2,
  }).format(safe);
};

const todayIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/**
 * Compute elapsed work seconds for an in-progress shift.
 *
 * Accepts a normalized entry (or the legacy localStorage shape via
 * `clockInAt`/`breaks`/`currentBreak` fields). Excludes time spent on breaks
 * (both completed and the in-progress one).
 */
export const computeElapsedSeconds = (entry, nowMs = Date.now()) => {
  if (!entry) return 0;
  const clockInIso = entry.clock_in_at || entry.clockInAt;
  if (!clockInIso) return 0;

  const clockInMs = new Date(clockInIso).getTime();
  if (Number.isNaN(clockInMs)) return 0;

  // If the shift is completed, use the saved hours value when present.
  const clockOutIso = entry.clock_out_at || entry.clockOutAt;
  const endMs = clockOutIso ? new Date(clockOutIso).getTime() : nowMs;
  const totalSec = Math.max(0, Math.floor((endMs - clockInMs) / 1000));

  let breakSec = 0;
  const breaksArr = Array.isArray(entry.breaks) ? entry.breaks : [];
  for (const b of breaksArr) {
    if (b?.start && b?.end) {
      const s = new Date(b.start).getTime();
      const e = new Date(b.end).getTime();
      if (e > s) breakSec += Math.floor((e - s) / 1000);
    }
  }
  const currentBreakStart =
    entry.current_break_start || entry.currentBreak?.start || null;
  if (currentBreakStart) {
    const s = new Date(currentBreakStart).getTime();
    if (!Number.isNaN(s)) {
      breakSec += Math.max(0, Math.floor((nowMs - s) / 1000));
    }
  }

  return Math.max(0, totalSec - breakSec);
};

/* ----------------------------------------------------------------------------
 * Active-entry lookups (Supabase = source of truth)
 * ------------------------------------------------------------------------- */

/**
 * Find this crew member's currently-open shift (status in_progress).
 * Returns null if none.
 */
export const fetchActiveEntry = async (crewId) => {
  if (!crewId) return null;
  const { data, error } = await supabase
    .from('time_entries')
    .select(TE_FIELDS)
    .eq('crew_id', crewId)
    .in('status', ['clocked_in', 'on_break'])
    .order('clock_in_at', { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error) {
    if (error.code === 'PGRST116') return null;
    throw wrapSchemaError(error);
  }
  cacheActiveEntry(crewId, data || null);
  return data || null;
};

/**
 * Bulk fetch all crew active entries — used by the page to hydrate every
 * card in one shot (instead of N round-trips).
 */
export const fetchAllActiveEntries = async () => {
  const { data, error } = await supabase
    .from('time_entries')
    .select(TE_FIELDS)
    .in('status', ['clocked_in', 'on_break'])
    .order('clock_in_at', { ascending: false });
  if (error) throw wrapSchemaError(error);
  // Latest entry wins per crew_id (defensive, in case duplicates ever exist).
  const map = new Map();
  for (const row of data || []) {
    if (!map.has(row.crew_id)) map.set(row.crew_id, row);
  }
  // Also refresh local cache for every known crew member (live roster, not
  // the legacy hardcoded list).
  const roster = await loadCrewRoster();
  for (const c of roster) {
    cacheActiveEntry(c.id, map.get(c.id) || null);
  }
  return Array.from(map.values());
};

/* ----------------------------------------------------------------------------
 * Mutations — every state change writes to Supabase synchronously.
 * ------------------------------------------------------------------------- */

/**
 * Clock in. Creates a new "clocked_in" row.
 *
 * jobInfo:
 *   - id      job UUID, or '__SHOP__' / '__ADMIN__', or null
 *   - name    display label (e.g. "Maple Bath" or "SHOP")
 */
export const startClock = async (
  crewId,
  { jobId, jobName, coId, coDesc } = {}
) => {
  // Defensive: if there's already an open entry for this crew, return it
  // instead of creating a duplicate (multi-device safety).
  const existing = await fetchActiveEntry(crewId);
  if (existing) {
    cacheActiveEntry(crewId, existing);
    return existing;
  }

  const crew = await getCrewMember(crewId);
  const virtual = isVirtualJobId(jobId);
  const row = {
    crew_id: crewId,
    name: crew?.name || crewId,
    job: jobName || (virtual ? 'No job assigned' : 'Active job'),
    job_id: virtual ? null : jobId || null,
    // CO scoping — only real jobs can carry a change-order tag.
    co_id: virtual ? null : coId || null,
    co_desc: virtual ? null : coDesc || null,
    date: todayIso(),
    clock_in_at: new Date().toISOString(),
    clock_out_at: null,
    breaks: [],
    current_break_start: null,
    status: 'clocked_in',
    hours: 0,
    cost: 0,
  };

  const { data, error } = await supabase
    .from('time_entries')
    .insert([row])
    .select(TE_FIELDS)
    .single();
  if (error) throw wrapSchemaError(error);
  cacheActiveEntry(crewId, data);
  return data;
};

/**
 * Begin a break on the active entry.
 */
export const startBreak = async (crewId) => {
  const active = await fetchActiveEntry(crewId);
  if (!active) return null;
  if (active.status !== 'clocked_in') return active;

  const { data, error } = await supabase
    .from('time_entries')
    .update({
      status: 'on_break',
      current_break_start: new Date().toISOString(),
    })
    .eq('id', active.id)
    .select(TE_FIELDS)
    .single();
  if (error) throw wrapSchemaError(error);
  cacheActiveEntry(crewId, data);
  return data;
};

/**
 * End an in-progress break.
 */
export const endBreak = async (crewId) => {
  const active = await fetchActiveEntry(crewId);
  if (!active) return null;
  if (active.status !== 'on_break' || !active.current_break_start) return active;

  const finishedBreak = {
    start: active.current_break_start,
    end: new Date().toISOString(),
  };
  const nextBreaks = [...(Array.isArray(active.breaks) ? active.breaks : []), finishedBreak];
  const { data, error } = await supabase
    .from('time_entries')
    .update({
      status: 'clocked_in',
      breaks: nextBreaks,
      current_break_start: null,
    })
    .eq('id', active.id)
    .select(TE_FIELDS)
    .single();
  if (error) throw wrapSchemaError(error);
  cacheActiveEntry(crewId, data);
  return data;
};

/**
 * Clock out. Closes any in-progress break, computes hours/cost, marks the
 * row as `completed`. Returns { entry, seconds, hours, cost, crew, jobName }.
 */
export const stopClock = async (crewId) => {
  const active = await fetchActiveEntry(crewId);
  if (!active) return null;

  // If still on break, close the break first (in-memory).
  let nextBreaks = Array.isArray(active.breaks) ? [...active.breaks] : [];
  let workEntry = { ...active };
  if (active.status === 'on_break' && active.current_break_start) {
    const finished = {
      start: active.current_break_start,
      end: new Date().toISOString(),
    };
    nextBreaks = [...nextBreaks, finished];
    workEntry = {
      ...active,
      breaks: nextBreaks,
      current_break_start: null,
      status: 'clocked_in',
    };
  }

  const seconds = computeElapsedSeconds(workEntry);
  const hours = Math.round((seconds / 3600) * 100) / 100;
  const crew = await getCrewMember(crewId);
  const rate = crew?.rate || 0;
  const cost = Math.round(hours * rate * 100) / 100;

  const { data, error } = await supabase
    .from('time_entries')
    .update({
      status: 'completed',
      breaks: nextBreaks,
      current_break_start: null,
      clock_out_at: new Date().toISOString(),
      hours,
      cost,
    })
    .eq('id', active.id)
    .select(TE_FIELDS)
    .single();
  if (error) throw wrapSchemaError(error);

  cacheActiveEntry(crewId, null);

  return {
    entry: data,
    seconds,
    hours,
    cost,
    crew,
    jobName: data?.job || workEntry.job || 'No job assigned',
  };
};

/**
 * Cancel an in-progress shift without saving — for emergency reset only.
 */
export const cancelClock = async (crewId) => {
  const active = await fetchActiveEntry(crewId);
  if (!active) return null;
  const { error } = await supabase
    .from('time_entries')
    .delete()
    .eq('id', active.id);
  if (error) throw wrapSchemaError(error);
  cacheActiveEntry(crewId, null);
  return true;
};

/* ----------------------------------------------------------------------------
 * Reads — list / today / weekly
 * ------------------------------------------------------------------------- */

/**
 * Fetch entries (default: only completed, latest first). Pass
 * `includeOpen: true` to include in-progress shifts (used for live displays).
 */
export const fetchTimeEntries = async ({
  crewId = null,
  fromDate = null,
  toDate = null,
  search = '',
  includeOpen = false,
} = {}) => {
  let q = supabase.from('time_entries').select(TE_FIELDS);
  if (!includeOpen) q = q.eq('status', 'completed');
  if (crewId) q = q.eq('crew_id', crewId);
  if (fromDate) q = q.gte('date', fromDate);
  if (toDate) q = q.lte('date', toDate);
  q = q.order('created_at', { ascending: false });
  const { data, error } = await q;
  if (error) throw wrapSchemaError(error);
  let list = data || [];
  if (search) {
    const s = search.trim().toLowerCase();
    list = list.filter((e) => {
      const hay = [e?.name, e?.job, e?.date].filter(Boolean).join(' ').toLowerCase();
      return hay.includes(s);
    });
  }
  return list;
};

export const fetchTodayEntries = async () =>
  fetchTimeEntries({ fromDate: todayIso(), toDate: todayIso() });

/* ----------------------------------------------------------------------------
 * Aggregations
 * ------------------------------------------------------------------------- */
export const computeJobLaborCosts = (entries = []) => {
  const map = new Map();
  for (const e of entries) {
    const key = e?.job || 'Unassigned';
    const cur = map.get(key) || { job: key, hours: 0, cost: 0, entries: 0 };
    cur.hours += Number(e?.hours) || 0;
    cur.cost += Number(e?.cost) || 0;
    cur.entries += 1;
    map.set(key, cur);
  }
  return Array.from(map.values()).sort((a, b) => b.cost - a.cost);
};

const startOfThisWeekIso = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  // Monday-start week
  const day = (d.getDay() + 6) % 7; // 0 = Mon
  d.setDate(d.getDate() - day);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

export const fetchWeeklySummary = async () => {
  const fromDate = startOfThisWeekIso();
  const entries = await fetchTimeEntries({ fromDate, toDate: todayIso() });
  const roster = await loadCrewRoster();
  const map = new Map();
  for (const c of roster) map.set(c.id, { crew: c, hours: 0, cost: 0, entries: 0 });
  for (const e of entries) {
    const cur = map.get(e?.crew_id);
    if (!cur) continue;
    cur.hours += Number(e?.hours) || 0;
    cur.cost += Number(e?.cost) || 0;
    cur.entries += 1;
  }
  return { fromDate, toDate: todayIso(), rows: Array.from(map.values()) };
};

/* ----------------------------------------------------------------------------
 * Edits / deletes (used in History tab) — limited to completed entries.
 * ------------------------------------------------------------------------- */
/* ----------------------------------------------------------------------------
 * Helpers — recompute hours/cost from clock-in/clock-out timestamps and breaks.
 *
 * Returns { hours: number, cost: number } where:
 *   hours = (out − in − Σbreak duration) in decimal hours, never negative.
 *   cost  = hours * crew.rate (default 0 if crew unknown).
 * ------------------------------------------------------------------------- */
export const recomputeFromTimes = (crewId, clockInIso, clockOutIso, breaks = []) => {
  if (!clockInIso || !clockOutIso) return { hours: 0, cost: 0 };
  const inMs = new Date(clockInIso).getTime();
  const outMs = new Date(clockOutIso).getTime();
  if (Number.isNaN(inMs) || Number.isNaN(outMs) || outMs <= inMs) {
    return { hours: 0, cost: 0 };
  }
  let breakSec = 0;
  for (const b of Array.isArray(breaks) ? breaks : []) {
    if (b?.start && b?.end) {
      const s = new Date(b.start).getTime();
      const e = new Date(b.end).getTime();
      if (e > s) breakSec += Math.floor((e - s) / 1000);
    }
  }
  const totalSec = Math.max(0, Math.floor((outMs - inMs) / 1000) - breakSec);
  const hours = Math.round((totalSec / 3600) * 100) / 100;
  // Synchronous helper — relies on the cached roster being warm. Callers
  // (e.g. EditTimeEntryDialog) mount via useCrewRoster() before this runs,
  // which guarantees the cache is populated.
  const crew = findCrewMember(crewId, getCachedCrewRoster());
  const rate = crew?.rate || 0;
  const cost = Math.round(hours * rate * 100) / 100;
  return { hours, cost };
};

/* ----------------------------------------------------------------------------
 * Manual entry creation (admin-only — gated at the UI layer).
 *
 * Use this to backfill missed shifts without going through the live clock.
 * Required: crewId, jobLabel, clockInAt (ISO), clockOutAt (ISO).
 * Optional: jobId (real job UUID, null for SHOP/ADMIN), date, hours/costOverride.
 * ------------------------------------------------------------------------- */
export const createManualTimeEntry = async ({
  crewId,
  jobId = null,
  jobName = '',
  date = null,
  clockInAt,
  clockOutAt,
  breaks = [],
  hoursOverride = null,
  costOverride = null,
}) => {
  if (!crewId) throw new Error('Crew is required.');
  if (!clockInAt || !clockOutAt) {
    throw new Error('Clock-in and clock-out times are required.');
  }
  const inMs = new Date(clockInAt).getTime();
  const outMs = new Date(clockOutAt).getTime();
  if (Number.isNaN(inMs) || Number.isNaN(outMs)) {
    throw new Error('Invalid clock times.');
  }
  if (outMs <= inMs) {
    throw new Error('Clock-out must be after clock-in.');
  }

  const crew = await getCrewMember(crewId);
  const computed = recomputeFromTimes(crewId, clockInAt, clockOutAt, breaks);
  const hours = hoursOverride !== null && hoursOverride !== ''
    ? Math.max(0, Number(hoursOverride) || 0)
    : computed.hours;
  const cost = costOverride !== null && costOverride !== ''
    ? Math.max(0, Number(costOverride) || 0)
    : computed.cost;

  const isoDate = (date && /^\d{4}-\d{2}-\d{2}/.test(date))
    ? date.slice(0, 10)
    : new Date(clockInAt).toISOString().slice(0, 10);

  const row = {
    crew_id: crewId,
    name: crew?.name || crewId,
    job: jobName || 'No job assigned',
    job_id: jobId || null,
    date: isoDate,
    clock_in_at: new Date(clockInAt).toISOString(),
    clock_out_at: new Date(clockOutAt).toISOString(),
    breaks: Array.isArray(breaks) ? breaks : [],
    current_break_start: null,
    status: 'completed',
    hours,
    cost,
  };

  const { data, error } = await supabase
    .from('time_entries')
    .insert([row])
    .select(TE_FIELDS)
    .single();
  if (error) throw wrapSchemaError(error);
  return data;
};

export const deleteTimeEntry = async (id) => {
  const { error } = await supabase.from('time_entries').delete().eq('id', id);
  if (error) throw wrapSchemaError(error);
  return true;
};

/**
 * Update a completed entry. Admin manual edits can change crew, job link,
 * clock-in/out timestamps, or override hours/cost. When clock times are
 * changed and hours/cost are not explicitly overridden, we recompute.
 *
 * Accepted patch keys:
 *   crew_id, name, job, job_id, date,
 *   clock_in_at, clock_out_at, breaks,
 *   hours, cost
 */
export const updateTimeEntry = async (id, updates = {}) => {
  if (!id) throw new Error('Entry id required.');
  // Fetch current row so we can recompute when only clock times change.
  const { data: existing, error: fetchErr } = await supabase
    .from('time_entries')
    .select(TE_FIELDS)
    .eq('id', id)
    .single();
  if (fetchErr) throw wrapSchemaError(fetchErr);

  const safe = {};
  if ('name' in updates) safe.name = String(updates.name || '');
  if ('job' in updates) safe.job = String(updates.job || '');
  if ('job_id' in updates)
    safe.job_id = updates.job_id == null || updates.job_id === '' ? null : String(updates.job_id);
  if ('crew_id' in updates && updates.crew_id) safe.crew_id = String(updates.crew_id);
  if ('date' in updates && updates.date) safe.date = String(updates.date).slice(0, 10);
  if ('clock_in_at' in updates) {
    safe.clock_in_at = updates.clock_in_at
      ? new Date(updates.clock_in_at).toISOString()
      : null;
  }
  if ('clock_out_at' in updates) {
    safe.clock_out_at = updates.clock_out_at
      ? new Date(updates.clock_out_at).toISOString()
      : null;
  }
  if ('breaks' in updates) {
    safe.breaks = Array.isArray(updates.breaks) ? updates.breaks : [];
  }

  // Recompute hours/cost when clock times changed and caller did not
  // explicitly pass hours/cost overrides.
  const clockTimesChanged = 'clock_in_at' in updates || 'clock_out_at' in updates;
  const hoursExplicit = 'hours' in updates && updates.hours !== '' && updates.hours !== null;
  const costExplicit = 'cost' in updates && updates.cost !== '' && updates.cost !== null;

  if (clockTimesChanged && (!hoursExplicit || !costExplicit)) {
    const effectiveCrewId = safe.crew_id || existing.crew_id;
    const effectiveIn = safe.clock_in_at !== undefined ? safe.clock_in_at : existing.clock_in_at;
    const effectiveOut = safe.clock_out_at !== undefined ? safe.clock_out_at : existing.clock_out_at;
    const effectiveBreaks = 'breaks' in safe ? safe.breaks : existing.breaks || [];
    const computed = recomputeFromTimes(
      effectiveCrewId,
      effectiveIn,
      effectiveOut,
      effectiveBreaks
    );
    if (!hoursExplicit) safe.hours = computed.hours;
    if (!costExplicit) safe.cost = computed.cost;
  }
  if (hoursExplicit) safe.hours = Math.max(0, Number(updates.hours) || 0);
  if (costExplicit) safe.cost = Math.max(0, Number(updates.cost) || 0);

  // Keep `date` in sync with clock_in_at when admin only changed times.
  if ('clock_in_at' in updates && !('date' in updates) && safe.clock_in_at) {
    safe.date = new Date(safe.clock_in_at).toISOString().slice(0, 10);
  }

  // Keep `name` in sync if crew_id changed and name not explicitly provided.
  if ('crew_id' in updates && !('name' in updates)) {
    const crew = await getCrewMember(safe.crew_id);
    if (crew) safe.name = crew.name;
  }

  const { data, error } = await supabase
    .from('time_entries')
    .update(safe)
    .eq('id', id)
    .select(TE_FIELDS)
    .single();
  if (error) throw wrapSchemaError(error);
  return data;
};

/* ----------------------------------------------------------------------------
 * Active jobs picker — active jobs from `jobs` table + virtual SHOP/ADMIN.
 * ------------------------------------------------------------------------- */
export const fetchActiveJobsForPicker = async () => {
  const { data, error } = await supabase
    .from('jobs')
    .select('id,name,client,address,status,change_orders')
    .eq('status', 'active')
    .order('name', { ascending: true });
  if (error) throw error;
  return data || [];
};

/**
 * Sum logged labor (hours + cost) per change order for a set of CO ids.
 * Returns { [co_id]: { hours, cost, entries } }. Used by the Change Orders tab
 * to show T&M accrual against each CO (and its optional NTE cap).
 */
export const fetchCoLaborTotals = async (coIds = []) => {
  const ids = Array.from(new Set((coIds || []).filter(Boolean)));
  if (ids.length === 0) return {};
  const { data, error } = await supabase
    .from('time_entries')
    .select('co_id,hours,cost')
    .in('co_id', ids);
  if (error) throw error;
  const out = {};
  for (const id of ids) out[id] = { hours: 0, cost: 0, entries: 0 };
  for (const e of data || []) {
    const bucket = out[e.co_id];
    if (!bucket) continue;
    bucket.hours += Number(e.hours) || 0;
    bucket.cost += Number(e.cost) || 0;
    bucket.entries += 1;
  }
  return out;
};

/* ----------------------------------------------------------------------------
 * Backwards-compat shim — older callers import readClockState. We keep a
 * synchronous helper that returns the cached entry only. Use
 * `fetchActiveEntry(crewId)` for an authoritative read.
 * ------------------------------------------------------------------------- */
export const readClockState = (crewId) => {
  const cached = readCachedActiveEntry(crewId);
  if (!cached) return null;
  // Map Supabase shape → legacy localStorage shape so any legacy reader still works.
  return {
    status: cached.status,
    clockInAt: cached.clock_in_at,
    jobId: cached.job_id,
    jobName: cached.job,
    breaks: Array.isArray(cached.breaks) ? cached.breaks : [],
    currentBreak: cached.current_break_start
      ? { start: cached.current_break_start }
      : null,
    id: cached.id,
  };
};
